/**
 * CB2 Studio preview postMessage protocol.
 *
 * Mirror of the dashboard-side protocol (poc-content-builder preview-iframe.util).
 * The webapp plays the role of the embedded iframe: it receives commands from the
 * dashboard (parent window) and emits selection events back.
 *
 * All messages carry `source: 'content-builder'` (kept identical to the POC so the
 * dashboard sender can be reused as-is). The webapp replies use
 * `source: 'dood-front'` to distinguish outbound events.
 */

export const CB2_PREVIEW_MESSAGE_SOURCE_IN = 'content-builder' as const;
export const CB2_PREVIEW_MESSAGE_SOURCE_OUT = 'dood-front' as const;

export type Cb2PreviewViewportPreset = string;

export interface Cb2PreviewMarketplaceLocale {
  id: string;
  locale: string;
  name: string;
}

export interface Cb2PreviewMarketplaceStub {
  id: string;
  name: string;
  content_builder_version: 'v2';
  available: boolean;
  cart_scope: string;
  currency: string;
  country: string;
  tips_allowed: boolean;
  capabilities: Array<{ type: string }>;
  distribution_modes: Array<Record<string, unknown>>;
  locales: Cb2PreviewMarketplaceLocale[];
  default_locale: Cb2PreviewMarketplaceLocale;
  domains: string[];
  pages: unknown[];
  modals: unknown[];
  plugins: unknown[];
  user_interface?: {
    colors?: Record<string, Record<string, string>>;
    [key: string]: unknown;
  };
}

export function hasPreviewMarketplaceStub(
  payload: Cb2PreviewRuntimePayload | undefined,
): payload is Cb2PreviewRuntimePayload & {
  marketplace: Cb2PreviewMarketplaceStub;
} {
  const id = payload?.marketplace?.id;
  return typeof id === 'string' && id.trim().length > 0;
}

/** Typed scenario transport pushed with each preview refresh. */
export interface Cb2PreviewScenarioSelection {
  dimensionIndex: number;
  scenarioName: string;
}

export interface Cb2PreviewScenarioPayload {
  blocType: string;
  scenarioKey: string;
  selections: Cb2PreviewScenarioSelection[];
  /** Authoritative scenario content; omitted/invalid yields null in consumers. */
  data?: unknown;
}

/** Runtime spec payload pushed live by the dashboard (optimistic preview). */
export interface Cb2PreviewRuntimePayload {
  pages?: unknown[];
  blocks?: unknown[];
  user_interface?: Record<string, unknown>;
  /** Resolved param catalog (optimistic — avoids a GET compile round-trip). */
  paramValues?: Record<string, unknown>;
  paramCatalog?: Record<string, { type?: string }>;
  marketplace?: Cb2PreviewMarketplaceStub;
  modals?: unknown[];
  previewScenarios?: Readonly<Record<string, Cb2PreviewScenarioPayload>>;
  /** @deprecated Use {@link previewScenarios} keyed by draft bloc id. */
  previewScenario?: Cb2PreviewScenarioPayload;
}

/** Inbound messages (dashboard → webapp iframe). */
export type Cb2PreviewInboundMessage =
  | {
      source: typeof CB2_PREVIEW_MESSAGE_SOURCE_IN;
      type: 'refresh-preview';
      marketplaceId: string;
      pageKey: string;
      lang: string;
      /** Preview device preset for `$cases` device branches (e.g. mobile/desktop). */
      device?: Cb2PreviewViewportPreset;
      payload?: Cb2PreviewRuntimePayload;
    }
  | {
      source: typeof CB2_PREVIEW_MESSAGE_SOURCE_IN;
      type: 'set-preview-locale';
      lang: string;
      /** Keep device in sync so locale changes do not resolve against a stale preset. */
      device?: Cb2PreviewViewportPreset;
      payload?: Cb2PreviewRuntimePayload;
    }
  | {
      source: typeof CB2_PREVIEW_MESSAGE_SOURCE_IN;
      type: 'set-preview-viewport';
      preset: Cb2PreviewViewportPreset;
      width: number;
      height: number;
      /** Optional payload so viewport changes can re-resolve `$cases` like locale. */
      payload?: Cb2PreviewRuntimePayload;
    }
  | {
      source: typeof CB2_PREVIEW_MESSAGE_SOURCE_IN;
      type: 'set-preview-editor-pick';
      enabled: boolean;
    }
  | {
      // Highlight a specific block instance in the preview (no scroll — kiosk
      // viewports must keep their layout). `blocKey` is the draft block key
      // (webapp block id) — unique per instance, so several blocks of the same
      // type can be targeted independently.
      source: typeof CB2_PREVIEW_MESSAGE_SOURCE_IN;
      type: 'scroll-to-bloc';
      blocKey: string;
    }
  | {
      source: typeof CB2_PREVIEW_MESSAGE_SOURCE_IN;
      type: 'cb2:open-modal';
      handle: string;
    }
  | {
      source: typeof CB2_PREVIEW_MESSAGE_SOURCE_IN;
      type: 'cb2:close-modals';
    };

/** Outbound messages (webapp iframe → dashboard). */
export type Cb2PreviewOutboundMessage =
  | {
      source: typeof CB2_PREVIEW_MESSAGE_SOURCE_OUT;
      type: 'cb2:preview-ready';
      marketplaceId?: string;
      pageKey?: string;
    }
  | {
      source: typeof CB2_PREVIEW_MESSAGE_SOURCE_OUT;
      type: 'select-bloc';
      blocKey: string;
    };

const CB2_PREVIEW_INBOUND_TYPES = new Set<Cb2PreviewInboundMessage['type']>([
  'refresh-preview',
  'set-preview-locale',
  'set-preview-viewport',
  'set-preview-editor-pick',
  'scroll-to-bloc',
  'cb2:open-modal',
  'cb2:close-modals',
]);

export function isCb2PreviewInboundMessage(data: unknown): data is Cb2PreviewInboundMessage {
  if (!data || typeof data !== 'object') return false;
  const source = (data as Record<string, unknown>)['source'];
  const type = (data as Record<string, unknown>)['type'];
  return (
    source === CB2_PREVIEW_MESSAGE_SOURCE_IN &&
    typeof type === 'string' &&
    CB2_PREVIEW_INBOUND_TYPES.has(type as Cb2PreviewInboundMessage['type'])
  );
}
